/**
 * Report Collaboration - Comments, review workflow, and status management
 */

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { updateReportStatus } from '../../api/reports';
import { EnhancedCommentsPanel } from '../Review';
import {
  MessageSquare,
  CheckCircle,
  Clock,
  FileCheck,
  Archive,
} from 'lucide-react';

interface ReportCollaborationProps {
  reportId: string;
  currentStatus?: string;
  onStatusChange?: (newStatus: string) => void;
}

export function ReportCollaboration({ reportId, currentStatus, onStatusChange }: ReportCollaborationProps) {
  const queryClient = useQueryClient();

  const statusMutation = useMutation({
    mutationFn: (status: 'draft' | 'in_review' | 'approved' | 'submitted' | 'archived') =>
      updateReportStatus(reportId, status),
    onSuccess: (updatedReport) => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      onStatusChange?.(updatedReport.status);
    },
  });

  const statusOptions = [
    { value: 'draft', label: 'Draft', icon: MessageSquare, color: 'yellow' },
    { value: 'in_review', label: 'In Review', icon: Clock, color: 'blue' },
    { value: 'approved', label: 'Approved', icon: CheckCircle, color: 'green' },
    { value: 'submitted', label: 'Submitted to Authorities', icon: FileCheck, color: 'purple' },
    { value: 'archived', label: 'Archived', icon: Archive, color: 'gray' },
  ];

  const currentStatusOption = statusOptions.find(s => s.value === currentStatus) || statusOptions[0];
  const StatusIcon = currentStatusOption.icon;

  return (
    <div className="space-y-6">
      {/* Status Management */}
      <Card title="Report Status" className="dark:bg-theme-paper dark:border-theme-border">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <StatusIcon className={`h-6 w-6 text-${currentStatusOption.color}-600 dark:text-${currentStatusOption.color}-400`} />
            <div>
              <div className="font-medium text-gray-900 dark:text-theme-text">Current Status</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">{currentStatus || 'draft'}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => statusMutation.mutate(option.value as any)}
                  disabled={currentStatus === option.value}
                  className={`px-4 py-3 border-2 rounded-lg transition-all flex items-center gap-2 ${
                    currentStatus === option.value
                      ? `border-${option.color}-500 dark:border-${option.color}-400 bg-${option.color}-50 dark:bg-${option.color}-900/20`
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-theme-paper'
                  } text-gray-900 dark:text-theme-text`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>

          {/* Status Workflow Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
            <p className="text-blue-900 dark:text-blue-300 font-medium mb-1">Review Workflow</p>
            <div className="text-blue-700 dark:text-blue-400 space-y-1">
              <div>1. Draft → In Review (submit for review)</div>
              <div>2. In Review → Approved (after professional review)</div>
              <div>3. Approved → Submitted (after submission to authorities)</div>
              <div>4. Submitted → Archived (when period ends)</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Enhanced Comments Panel */}
      <EnhancedCommentsPanel
        reportId={reportId}
        onCommentChange={() => {
          queryClient.invalidateQueries({ queryKey: ['report-comments', reportId] });
        }}
      />
    </div>
  );
}
